import { getBrowserSessionMetadata } from "../services/deviceIdentity";

// How long the shell rings for an incoming call, measured from the server's
// initiatedAt so a late-delivered signal (tab was asleep, socket reconnect)
// does not ring for a full 45s after the caller already gave up.
export const INCOMING_CALL_RING_MS = 45000;
export const INCOMING_CALL_MIN_RING_MS = 5000;

export const SOCKET_AUTH_ERROR_PREFIX = "SOCKET_AUTH_";
// A server-side configuration failure is not a statement about this session;
// signing the user out would only bounce them into the same error.
const SOCKET_AUTH_NON_SESSION_CODES = new Set(["SOCKET_AUTH_CONFIG_ERROR"]);

export function getRemainingRingMs(initiatedAt, now = Date.now()) {
  const startedAt =
    initiatedAt instanceof Date ? initiatedAt.getTime() : Date.parse(initiatedAt);
  if (!Number.isFinite(startedAt)) return INCOMING_CALL_RING_MS;
  const remaining = INCOMING_CALL_RING_MS - (now - startedAt);
  return Math.min(
    INCOMING_CALL_RING_MS,
    Math.max(INCOMING_CALL_MIN_RING_MS, remaining),
  );
}

export function getCallerDisplayName(caller) {
  const fullName =
    `${caller?.first_name || caller?.firstName || ""} ${caller?.last_name || caller?.lastName || ""}`.trim();
  return fullName || caller?.username || "Someone";
}

/**
 * Whether a call session descriptor from the server (call_accepted's
 * acceptedSession / call:answered_elsewhere's answeredSession) refers to this
 * browser tab. Matched on sessionId when the server sent one, else deviceId;
 * a descriptor without either cannot be told apart and counts as ours.
 */
export function sessionMatchesThisTab(session, mine = getBrowserSessionMetadata()) {
  if (!session || typeof session !== "object") return true;
  const sessionId = session.sessionId || session.participantSessionId || "";
  if (sessionId) return `${sessionId}` === `${mine?.sessionId || ""}`;
  if (session.deviceId) return `${session.deviceId}` === `${mine?.deviceId || ""}`;
  return true;
}

/**
 * Classify a failed POST /calls/:id/accept:
 *   network            – no response at all (offline, timeout): retryable
 *   unauthorized       – 401: the session is gone
 *   answered_elsewhere – 409 CALL_ANSWERED_ELSEWHERE (or the legacy message)
 *   failed             – anything else
 */
export function classifyAcceptFailure(error) {
  const response = error?.response;
  if (!response) return "network";
  const status = Number(response.status || 0);
  if (status === 401) return "unauthorized";
  const code = `${response.data?.code || ""}`;
  const message = `${response.data?.message || ""}`;
  if (
    status === 409 &&
    (code === "CALL_ANSWERED_ELSEWHERE" || /already (answered|active)/i.test(message))
  ) {
    return "answered_elsewhere";
  }
  return "failed";
}

/**
 * The SOCKET_AUTH_* code carried by a socket.io connect_error (server
 * middleware errors arrive as `error.data.code`; some transports only keep
 * the message), or "" when the failure is not an authentication refusal that
 * should end the session.
 */
export function getSocketAuthErrorCode(error) {
  const candidates = [error?.data?.code, error?.message];
  for (const candidate of candidates) {
    const value = `${candidate || ""}`.trim();
    if (value.startsWith(SOCKET_AUTH_ERROR_PREFIX)) {
      return SOCKET_AUTH_NON_SESSION_CODES.has(value) ? "" : value;
    }
  }
  return "";
}
